import React, { useContext } from "react";
import { useParams } from "react-router-dom";
import { HouseContext } from "../components/HouseContext";
import ImageGallery from "react-image-gallery";
import BookingInfo from "../components/BookingInfo";
import "react-image-gallery/styles/css/image-gallery.css";
import { MdLocationOn, MdKingBed, MdBathtub, MdSquareFoot, MdCalendarToday } from "react-icons/md";
import { RiArrowLeftLine } from "react-icons/ri";
import { Link } from "react-router-dom";

const Property = () => {
    const { id } = useParams();
    const { houses, loading } = useContext(HouseContext);

    const house = houses.find((house) => house.id.toLowerCase() === id.toLowerCase());

    if (loading) {
        return (
            <section className="flex min-h-screen items-center justify-center bg-[#f6f1e8]">
                <p className="text-slate-600">Loading...</p>
            </section>
        );
    }

    if (!house) {
        return (
            <section className="flex min-h-screen items-center justify-center bg-[#f6f1e8]">
                <p className="text-slate-600">House not found</p>
            </section>
        );
    }

    const images = house.imageGallery.map((image, index) => ({
        original: image,
        thumbnail: house.imageGalleryThumbs && house.imageGalleryThumbs[index] 
            ? house.imageGalleryThumbs[index] 
            : image,
    }));

    return (
        <section className="bg-[#f6f1e8] pb-20 pt-8">
            <div className="container mx-auto max-w-[1120px] px-4">
                <Link to="/" className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition duration-300 hover:-translate-y-0.5 hover:text-teal-700">
                    <RiArrowLeftLine />
                    Back to rentals
                </Link>

                <div className="relative overflow-hidden rounded-2xl shadow-[0_24px_90px_rgba(15,23,42,0.18)] animate-fade-up">
                    <ImageGallery items={images} autoPlay={true} />
                    <div className="absolute top-6 left-6 rounded-full bg-white/85 px-4 py-2 text-sm font-bold text-slate-900 shadow-lg backdrop-blur">
                        {house.rentalType}
                    </div>
                </div>
                
                <div className="mt-8 rounded-2xl border border-white/70 bg-white/82 p-6 shadow-[0_18px_60px_rgba(15,23,42,0.10)] backdrop-blur md:p-8">
                    <div>
                        <p className="mb-2 text-sm font-bold uppercase tracking-[0.22em] text-teal-700">Lavallette weekly rental</p>
                        <h1 className="text-4xl font-bold tracking-tight text-slate-950 mb-3 md:text-5xl">{house.name}</h1>
                        <div className="flex items-start gap-2 text-slate-600 mb-5">
                            <MdLocationOn className="text-teal-700 text-xl mt-0.5 flex-shrink-0" />
                            <p className="text-lg">{house.address}</p>
                        </div>
                    </div>
                    
                    <p className="text-slate-700 leading-relaxed text-lg">{house.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                        <div className="flex items-center gap-3 rounded-xl border border-teal-100 bg-teal-50/80 p-4">
                            <MdKingBed className="text-3xl text-teal-700" />
                            <div>
                                <p className="text-sm text-slate-500">Bedrooms</p>
                                <p className="text-xl font-bold text-slate-950">{house.bedrooms}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50/80 p-4">
                            <MdBathtub className="text-3xl text-sky-700" />
                            <div>
                                <p className="text-sm text-slate-500">Bathrooms</p>
                                <p className="text-xl font-bold text-slate-950">{house.bathrooms}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/80 p-4">
                            <MdSquareFoot className="text-3xl text-emerald-700" />
                            <div>
                                <p className="text-sm text-slate-500">Square Feet</p>
                                <p className="text-xl font-bold text-slate-950">{house.surface}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/80 p-4">
                            <MdCalendarToday className="text-3xl text-amber-700" />
                            <div>
                                <p className="text-sm text-slate-500">Year Built</p>
                                <p className="text-xl font-bold text-slate-950">{house.year}</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-8 flex items-center space-x-4 rounded-2xl border border-white/70 bg-white/82 p-6 shadow-[0_18px_60px_rgba(15,23,42,0.10)] backdrop-blur">
                    <img
                        src={house.agent.image}
                        alt={house.agent.name}
                        className="w-20 h-20 rounded-full border-4 border-white shadow-md"
                    />
                    <div>
                        <p className="text-sm text-slate-500 mb-1">Property Managed By</p>
                        <p className="font-bold text-lg text-slate-950">{house.agent.name}</p>
                        <a href={`tel:${house.agent.phone}`} className="text-teal-700 font-semibold hover:text-slate-950 transition">
                            {house.agent.phone}
                        </a>
                    </div>
                </div>
                
                <BookingInfo house={house} />
                
                <div className="mt-12">
                    <div className="mb-6 flex flex-col justify-between gap-3 md:flex-row md:items-end">
                        <div>
                            <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-700">Calendar</p>
                            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">Weekly Availability</h2>
                        </div>
                        <p className="max-w-xl text-slate-600">Each week shows check-in, check-out, rate, and current booking status.</p>
                    </div>
                    
                    {/* Group rentals by month */}
                    {Object.entries(
                        house.weeklyRentals.reduce((acc, rental) => {
                            const month = new Date(rental.checkInDate).toLocaleDateString('en-US', { 
                                month: 'long', 
                                year: 'numeric' 
                            });
                            if (!acc[month]) acc[month] = [];
                            acc[month].push(rental);
                            return acc;
                        }, {})
                    ).map(([month, rentals]) => (
                        <div key={month} className="mb-8">
                            {/* Month Header - Calendar Style */}
                            <div className="rounded-t-2xl bg-slate-950 px-6 py-4 text-white shadow-lg">
                                <h3 className="text-2xl font-bold">{month}</h3>
                            </div>
                            
                            {/* Weeks Grid */}
                            <div className="rounded-b-2xl border border-white/70 bg-white/82 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                    {rentals.map((rental, index) => (
                                        <div
                                            key={index}
                                            className={`rounded-xl border p-4 transition-all duration-300 ${
                                                rental.available 
                                                    ? "border-emerald-200 bg-emerald-50/90 hover:-translate-y-1 hover:border-emerald-400 hover:shadow-lg" 
                                                    : "border-slate-200 bg-slate-100/80 opacity-65"
                                            }`}
                                        >
                                            {/* Week dates - compact single line */}
                                            <div className="text-center mb-3 pb-3 border-b border-slate-200">
                                                <p className={`text-xs font-semibold uppercase mb-1 ${rental.available ? "text-slate-500" : "text-slate-400"}`}>Check-in</p>
                                                <p className={`font-bold text-base ${rental.available ? "text-slate-800" : "text-slate-500"}`}>
                                                    {new Date(rental.checkInDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </p>
                                                <p className="text-slate-400 text-xs my-1">to</p>
                                                <p className={`text-xs font-semibold uppercase mb-1 ${rental.available ? "text-slate-500" : "text-slate-400"}`}>Check-out</p>
                                                <p className={`font-bold text-base ${rental.available ? "text-slate-800" : "text-slate-500"}`}>
                                                    {new Date(rental.checkOutDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </p>
                                            </div>
                                            
                                            {/* Price */}
                                            <div className="text-center mb-3">
                                                <p className={`text-xs uppercase font-semibold mb-1 ${rental.available ? "text-slate-500" : "text-slate-400"}`}>Weekly Rate</p>
                                                <p className={`text-xl font-bold ${rental.available ? "text-teal-800" : "text-slate-500"}`}>
                                                    {rental.weeklyRate}
                                                </p>
                                            </div>
                                            
                                            {/* Availability Badge */}
                                            <div className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg ${
                                                rental.available 
                                                    ? "bg-emerald-600 text-white" 
                                                    : "bg-slate-500 text-white"
                                            }`}>
                                                <div className={`w-2 h-2 rounded-full bg-white ${rental.available ? "animate-pulse" : ""}`}></div>
                                                <p className="text-xs font-bold">
                                                    {rental.available ? "Available" : "Booked"}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}

                    <BookingInfo house={house} />
                </div>
            </div>
        </section>
    );
};

export default Property;
